import React, { useMemo, useState } from 'react';
import MarketingHeader from '../../components/MarketingHeader';
import {
  isMobileFeedImportEnabled,
  useDeleteSocialAccount,
  useInitSocialOAuth,
  useSetMobileFeedImportEnabled,
  useSocialAccounts,
  type SocialPlatform,
} from '../../hooks/useSocial';
import { DangerButton, PrimaryButton, SecondaryButton } from '../../styles/marketingSharedStyles';
import { Card, CardTitle, Input, Item, List, Page, Row, Select, Subtle } from './socialStyles';

const SocialAccountsPage: React.FC = () => {
  const accounts = useSocialAccounts();
  const initOAuth = useInitSocialOAuth();
  const deleteAccount = useDeleteSocialAccount();
  const setMobileFeedImport = useSetMobileFeedImportEnabled();
  const [platform, setPlatform] = useState<SocialPlatform>('facebook');
  const [redirectUri, setRedirectUri] = useState('');
  const [oauthError, setOauthError] = useState<string | null>(null);

  const defaultRedirect = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/marketing/social/accounts`;
  }, []);

  const onConnect = async () => {
    try {
      setOauthError(null);
      const result = await initOAuth.mutateAsync({
        platform,
        redirectUri: redirectUri.trim() || `${window.location.origin}/functions/v1/social-oauth-callback`,
      });
      window.location.href = result.authUrl;
    } catch (error) {
      setOauthError(error instanceof Error ? error.message : 'Unable to start account connection.');
    }
  };

  return (
    <Page>
      <MarketingHeader activeTab="social" />

      <Card>
        <CardTitle>Connect Social Account</CardTitle>
        <Row>
          <Select value={platform} onChange={(event) => setPlatform(event.target.value as SocialPlatform)}>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="youtube">YouTube</option>
          </Select>
          <Input
            placeholder="OAuth redirect URI"
            value={redirectUri}
            onChange={(event) => setRedirectUri(event.target.value)}
          />
          <PrimaryButton type="button" onClick={onConnect} disabled={initOAuth.isPending}>
            {initOAuth.isPending ? 'Connecting...' : 'Connect'}
          </PrimaryButton>
          <SecondaryButton type="button" onClick={() => setRedirectUri(defaultRedirect)}>
            Use app default
          </SecondaryButton>
        </Row>
        {oauthError ? <Subtle>{oauthError}</Subtle> : null}
        <Subtle>For OAuth callback URI, use your deployed edge function callback URL in production.</Subtle>
      </Card>

      <Card>
        <CardTitle>Connected Accounts</CardTitle>
        <Subtle style={{ marginBottom: '0.75rem' }}>
          Only @newchurchoffaith is imported into the mobile Social feed.
        </Subtle>
        {accounts.isLoading ? (
          <Subtle>Loading accounts...</Subtle>
        ) : (
          <List>
            {(accounts.data ?? []).map((account) => (
              <Item key={account.id}>
                <Row>
                  <strong>{account.display_name}</strong>
                  <span>{account.platform}</span>
                  <span>{account.username ?? account.account_external_id}</span>
                  <span>{account.status}</span>
                  {account.platform === 'instagram' ? (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--onSurface)' }}>
                      <input
                        type="checkbox"
                        checked={isMobileFeedImportEnabled(account.metadata)}
                        disabled={setMobileFeedImport.isPending}
                        onChange={(event) => {
                          setMobileFeedImport.mutate({
                            id: account.id,
                            metadata: account.metadata,
                            enabled: event.target.checked,
                          });
                        }}
                      />
                      Show on mobile Social feed
                    </label>
                  ) : null}
                  <DangerButton
                    type="button"
                    onClick={() => deleteAccount.mutate(account.id)}
                    disabled={deleteAccount.isPending}
                  >
                    Disconnect
                  </DangerButton>
                </Row>
              </Item>
            ))}
            {(accounts.data ?? []).length === 0 ? <Subtle>No connected accounts yet.</Subtle> : null}
          </List>
        )}
        {setMobileFeedImport.isError ? (
          <Subtle>
            {setMobileFeedImport.error instanceof Error
              ? setMobileFeedImport.error.message
              : 'Unable to update the mobile feed setting.'}
          </Subtle>
        ) : null}
      </Card>
    </Page>
  );
};

export default SocialAccountsPage;
